import { Globe } from 'lucide-react';
import { useTimezoneMismatch } from '@/hooks/useGlobalCompany';
import { getTimezoneGmtOffset } from '@/lib/dateUtils';

type TimezoneMode = 'company' | 'local';

interface TimezoneIndicatorProps {
  mode: TimezoneMode;
  className?: string;
}

/**
 * Subtle inline indicator showing which timezone dates are displayed in.
 * Only renders when the user's browser timezone differs from the company timezone.
 *
 * - mode="company" → "Showing company timezone (GMT-5)"
 * - mode="local"   → "Showing local timezone (GMT-4)"
 */
export function TimezoneIndicator({ mode, className }: TimezoneIndicatorProps) {
  const { isMismatch, userTz, companyTz } = useTimezoneMismatch();

  if (!isMismatch) return null;

  const tz = mode === 'company' ? companyTz : userTz;
  const offset = getTimezoneGmtOffset(tz);
  const label = mode === 'company' ? 'Company timezone' : 'Local timezone';

  return (
    <span className={`inline-flex items-center gap-1 text-xs select-none ${className ?? 'text-gray-400'}`} title={tz}>
      <Globe className="h-3 w-3" />
      <span className="sm:hidden leading-tight">{label}:<br />{offset}</span>
      <span className="hidden sm:inline">{label} ({offset})</span>
    </span>
  );
}
